import {
  addDays,
  differenceInMinutes,
  format,
  getHours,
  isAfter,
  isBefore,
  isValid,
  isWithinInterval,
  parse,
  set,
  subMinutes,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { IRestaurant } from '@rbi-ctg/store';
import { IOperatingHours } from 'generated/graphql-gateway';
import { ServiceMode } from 'state/service-mode/types';
import LocalStorage from 'utils/cognito/storage.web';
import { StorageKeys } from 'utils/local-storage';
import { isPickup } from 'utils/service-mode';

import {
  ALT_PARSE_FORMAT,
  DEFAULT_STORE_TIMEZONE,
  LOCALE_TIME_PARSE_FORMAT,
  MIDNIGHT,
  PARSE_FORMAT,
  TWELVE_HOUR_TIME_PARSE_FORMAT,
} from '../constants';
import { Days } from '../types';

/**
 * Returns the day that follows the given reference date in the store's timezone.
 * The returned value is a key from the Days enum that can be used to construct
 * operating hours keys (e.g., 'monOpen', 'tueClose').
 *
 * @param {Date} referenceDate - The reference date to get the next day from
 * @param {string} [storeTimeZone=DEFAULT_STORE_TIMEZONE] - The timezone of the store
 * @returns {string} A key from the Days enum representing the next day (e.g., 'mon', 'tue')
 */
export function nextDay(
  referenceDate: Date,
  storeTimeZone: string = DEFAULT_STORE_TIMEZONE
): string {
  const dateInStoreTimezone = utcToZonedTime(referenceDate.toISOString(), storeTimeZone);

  return Days[(dateInStoreTimezone.getDay() + 1) % 7];
}

/*
 * Returns opening hours for the day after the the `referenceDate`.
 */
export function getNextDayOpeningHours(
  hours: IOperatingHours,
  referenceDate: Date = new Date(),
  storeTimeZone: string = DEFAULT_STORE_TIMEZONE
): string {
  return hours[nextDay(referenceDate, storeTimeZone).concat('Open')];
}

/*
 * Returns closing hours for the day after the the `referenceDate`.
 */
export function getNextDayClosingHours(
  hours: IOperatingHours,
  referenceDate: Date = new Date(),
  storeTimeZone: string = DEFAULT_STORE_TIMEZONE
): string {
  return hours[nextDay(referenceDate, storeTimeZone).concat('Close')];
}

/**
 * Parses a time string from mdm
 *
 * time strings can come in three flavors, so we have to try to parse all three
 * * yyyy-MM-dd HH:mm:ss.SSSSSSS
 * * yyyy-MM-dd HH:mm:ss
 * * HH:mm:ss
 *
 */
export function parseMdmTimeString(timeString: string, referenceDate: Date = new Date()): Date {
  let formattedTime = parse(timeString, PARSE_FORMAT, referenceDate);

  if (!isValid(formattedTime)) {
    formattedTime = parse(timeString, ALT_PARSE_FORMAT, referenceDate);
  }

  if (!isValid(formattedTime)) {
    formattedTime = parse(timeString, LOCALE_TIME_PARSE_FORMAT, referenceDate);
  }

  return formattedTime;
}

/**
 * Displays the given time string in a human readable format, either in standard or
 * military time format based on the market configuration.
 * @param {string} timeString Time string.
 * @param {boolean | undefined} ignoreMdmTimeParse If true, will not parse the time string as mdm.
 * @returns {string} Human readable time string.
 */
interface IReadableHourProps {
  timeString: string;
  ignoreMdmTimeParse?: boolean;
  timeFormat?: string;
  storeTimeZone?: string;
}
export function readableHour({
  timeString,
  ignoreMdmTimeParse,
  timeFormat = TWELVE_HOUR_TIME_PARSE_FORMAT,
  storeTimeZone = DEFAULT_STORE_TIMEZONE,
}: IReadableHourProps): string {
  try {
    if (ignoreMdmTimeParse) {
      // This is a normal ISO8601 string where the date part matters.
      const dateInStoreTimeZone = utcToZonedTime(new Date(timeString).toISOString(), storeTimeZone);
      return format(dateInStoreTimeZone, timeFormat);
    }

    // This are opening or closing hours, we need to replace the fixed date part (i.e., 1970-01-01).
    return format(getDateForHours(timeString, storeTimeZone), timeFormat);
  } catch (error) {
    return 'hours not available';
  }
}

interface IReadableTimeInterval {
  fallbackMessage: string;
  open24HoursMessage?: string;
  timeFormat?: string;
}
export const readableTimeInterval = ({
  fallbackMessage,
  open24HoursMessage,
  timeFormat = TWELVE_HOUR_TIME_PARSE_FORMAT,
}: IReadableTimeInterval) => (fromTime: string, toTime: string) => {
  try {
    const readableFromHour = format(parseMdmTimeString(fromTime), timeFormat);
    const readableToHour = format(parseMdmTimeString(toTime), timeFormat);

    // we cannot simply check for equality because we only want to show
    // that a store is open 24 hours if the open/close times are both midnight
    // and not just any 24 hour interval.
    const is24HourInterval = readableFromHour === MIDNIGHT && readableToHour === MIDNIGHT;

    if (is24HourInterval && open24HoursMessage) {
      return open24HoursMessage;
    }

    return `${readableFromHour} - ${readableToHour}`;
  } catch (e) {
    return fallbackMessage;
  }
};

function getTodaysHoursOfOperation(
  hours: IOperatingHours,
  storeTimeZone: string = DEFAULT_STORE_TIMEZONE,
  now: Date = new Date(Date.now())
): [string, string] {
  if (!hours) {
    return ['', ''];
  }
  const nowInStoreTimeZone = utcToZonedTime(now.toISOString(), storeTimeZone);
  const dayName = Days[nowInStoreTimeZone.getDay()];
  const openHours: string = hours[dayName + 'Open'];
  const closeHours: string = hours[dayName + 'Close'];
  return [openHours, closeHours];
}

export function readableCloseHourToday(
  hours?: IOperatingHours | null,
  timeFormat?: string,
  storeTimeZone?: string | null
) {
  if (!hours) {
    return 'N/A';
  }

  const [, closeHours] = getTodaysHoursOfOperation(hours, storeTimeZone || undefined);

  if (!closeHours) {
    return 'N/A';
  }

  return readableHour({
    timeString: closeHours,
    timeFormat,
    storeTimeZone: storeTimeZone || undefined,
  });
}

/*
 * Given a time string of the format "yyyy-MM-dd HH:mm:ss" (usually something like `1970-01-01 23:59:00.0000000`),
 * return today's date, in the store timezone, with hours, minutes, and seconds from the time string.
 */
export function getDateForHours(
  openingOrClosingHours: string,
  storeTimeZone: string = DEFAULT_STORE_TIMEZONE
) {
  const nowInStoreTimeZone = utcToZonedTime(new Date().toISOString(), storeTimeZone);

  const parsedHours = parseMdmTimeString(openingOrClosingHours, nowInStoreTimeZone);

  parsedHours.setFullYear(nowInStoreTimeZone.getFullYear());
  parsedHours.setMonth(nowInStoreTimeZone.getMonth());
  parsedHours.setDate(nowInStoreTimeZone.getDate());

  return parsedHours;
}

// Check given time is within 20 minutes of closing
// and ensuring there is a gap closure of the next opening day
export function isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(
  hours: IOperatingHours,
  now: Date = new Date(Date.now()),
  storeTimeZone = DEFAULT_STORE_TIMEZONE,
  minutes = 20
) {
  const nowInStoreTimeZone = utcToZonedTime(now.toISOString(), storeTimeZone);

  const [, closeHours] = getTodaysHoursOfOperation(hours, storeTimeZone, now);
  const close = getDateForHours(closeHours, storeTimeZone);

  if (!isWithinInterval(nowInStoreTimeZone, { start: subMinutes(close, minutes), end: close })) {
    return true;
  }

  // 24 hour store check
  // Subtract one minute due from 00 to 59
  const FOR_24_HOURS_IN_MINUTES_BETWEEN_MONTHS = 60 * 24 - 1;
  const tomorrowDayName = nextDay(nowInStoreTimeZone, storeTimeZone);
  const tomorrowOpenHours = hours[`${tomorrowDayName}Open`];

  const open = getDateForHours(tomorrowOpenHours, storeTimeZone);

  // The comparison of today close and tmrw open hours
  return differenceInMinutes(close, open) === FOR_24_HOURS_IN_MINUTES_BETWEEN_MONTHS;
}

// TODO: Remove once LaunchDarklyFlag.ENABLE_CLOSING_TIME_DIALOG is removed
export function checkIfDateIsWithinCloseTimeAndMinutes(
  hours: IOperatingHours,
  now: Date = new Date(Date.now()),
  minutes = 60
) {
  const [, closeHours] = getTodaysHoursOfOperation(hours, undefined, now);
  let close = getDateForHours(closeHours);

  if (getHours(close) === 0) {
    close = addDays(close, 1);
  }

  return isWithinInterval(now, { start: subMinutes(close, minutes), end: close });
}

// Get hour in a.m or p.m. from HH:mm string
export const formatTime = (timeString: any) => {
  const [hourString, minute] = timeString.split(':');
  const hour = +hourString % 24;
  return (hour % 12 || 12) + ':' + minute + (hour < 12 ? ' a.m.' : ' p.m.');
};

/**
 * Returns the operating hours of the store for the provided service mode.
 * @param {IRestuarant} store The store from which to obtain operating hours.
 * @param {ServiceMode} serviceMode The service mode whose hours to get.
 * @returns {IOperatingHours | null | undefined} Store operating hours for the provided service mode if available or `null` or `undefined` otherwise.
 */
export function getStoreOperatingHoursForServiceMode(
  store: IRestaurant,
  serviceMode: ServiceMode
): IOperatingHours | null | undefined {
  switch (serviceMode) {
    case ServiceMode.DELIVERY:
      return store.deliveryHours;
    case ServiceMode.DRIVE_THRU:
      return store.driveThruHours;
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      return store.mobileOrderDriveThruHours;
    case ServiceMode.CURBSIDE:
      return store.curbsideHours;
    default:
      return store.diningRoomHours;
  }
}

/**
 * Retrieves the latest closing time among the service modes for a given restaurant.
 *
 * Iterates through all defined `ServiceMode` values and, depending on the `onlyPickUpServiceModes` flag,
 * filters to include only pickup-related modes. It collects the closing times for the current day,
 * excluding service modes that are 24 hours or have missing data.
 *
 * @param {Object} params - Parameters object.
 * @param {IRestaurant} params.restaurant - The restaurant whose service modes will be checked.
 * @param {boolean} params.onlyPickUpServiceModes - If true, only pickup service modes will be considered.
 *
 * @returns {Array<{ serviceMode: ServiceMode, closingTime: Date }>}
 * An array of service modes with their corresponding closing times.
 */
export const getRestaurantLatestClosingTime = ({
  restaurant,
  onlyPickUpServiceModes,
}: {
  restaurant: IRestaurant;
  onlyPickUpServiceModes: boolean;
}): { serviceMode: ServiceMode; closingTime: Date } | null => {
  const serviceModesClosingTime: { serviceMode: ServiceMode; closingTime: Date }[] = [];

  for (const serviceMode of Object.values(ServiceMode)) {
    const isPickUpServiceMode = isPickup(serviceMode);

    if (onlyPickUpServiceModes && !isPickUpServiceMode) {
      continue;
    }

    const operatingHours = getTodayRestaurantOperatingHoursByServiceMode({
      restaurant,
      serviceMode,
    });

    if (!operatingHours || !restaurant.timezone) {
      continue;
    }

    const { closingTime, open24hs } = operatingHours;

    // If there is any 24hs service mode, we don't return any closing time
    if (open24hs) {
      return null;
    }

    serviceModesClosingTime.push({
      serviceMode,
      closingTime,
    });
  }

  if (!serviceModesClosingTime.length) {
    return null;
  }

  return serviceModesClosingTime.reduce((latest, current) => {
    return isAfter(current.closingTime, latest.closingTime) ? current : latest;
  });
};

const getTodayRestaurantOperatingHoursByServiceMode = ({
  restaurant,
  serviceMode,
}: {
  restaurant: IRestaurant;
  serviceMode: ServiceMode;
}) => {
  let open24hs = false;
  const operatingHours = getStoreOperatingHoursForServiceMode(restaurant, serviceMode);

  if (!operatingHours || !restaurant.timezone) {
    return null;
  }

  const nowInRestaurantTZ = getCurrentDateInStoreTimeZone(restaurant);
  const [openingTimeStr, closingTimeStr] = getTodaysHoursOfOperation(
    operatingHours,
    restaurant.timezone
  );
  const [tomorrowOpeningTimeStr] = getTodaysHoursOfOperation(
    operatingHours,
    restaurant.timezone,
    addDays(nowInRestaurantTZ, 1)
  );

  if (!openingTimeStr || !closingTimeStr) {
    return null;
  }

  const [openingHour, openingMinute, openingSecond] = openingTimeStr.split(':').map(Number);
  const [closingHour, closingMinute, closingSecond] = closingTimeStr.split(':').map(Number);

  const openingTime = set(nowInRestaurantTZ, {
    hours: openingHour,
    minutes: openingMinute,
    seconds: openingSecond,
    milliseconds: 0,
  });

  // There are cases where, instead of having a closing time of 23:59, it is defined as 00:00.
  // In these cases, we need to add one day.
  const closingTime = addDays(
    set(nowInRestaurantTZ, {
      hours: closingHour,
      minutes: closingMinute,
      seconds: closingSecond,
      milliseconds: 0,
    }),
    closingHour + closingMinute + closingSecond === 0 ? 1 : 0
  );

  if (tomorrowOpeningTimeStr) {
    const [
      tomorrowOpeningHour,
      tomorrowOpeningMinute,
      tomorrowOpeningSecond,
    ] = tomorrowOpeningTimeStr.split(':').map(Number);

    const tomorrowOpeningTime = set(nowInRestaurantTZ, {
      hours: tomorrowOpeningHour,
      minutes: tomorrowOpeningMinute,
      seconds: tomorrowOpeningSecond,
      milliseconds: 0,
    });

    if (Math.abs(differenceInMinutes(closingTime, tomorrowOpeningTime)) <= 1) {
      open24hs = true;
    }
  }

  return {
    openingTime,
    closingTime,
    open24hs,
  };
};

export function getCurrentDateInStoreTimeZone(store: IRestaurant): Date {
  if (store.localTime) {
    return new Date(store.localTime);
  }

  const nowInUTC = new Date().toISOString();
  const storeTimeZone = store.timezone || DEFAULT_STORE_TIMEZONE;

  return utcToZonedTime(nowInUTC, storeTimeZone);
}

export function getStoreLocalTime() {
  return LocalStorage.getItem<string>(StorageKeys.SELECTED_STORE_LOCAL_TIME);
}

export function setStoreLocalTime(time: string) {
  LocalStorage.setItem(StorageKeys.SELECTED_STORE_LOCAL_TIME, time);
}

export function clearStoreLocalTime() {
  LocalStorage.removeItem(StorageKeys.SELECTED_STORE_LOCAL_TIME);
}

/**
 * IMPORTANT: This does not account for operating hour overrides.
 *
 * TODO: Implement this in the BE and remove this.
 *
 * Returns the next opening hours for a given store and service mode.
 * If there aren't opening hours set for today, returns tomorrow's opening hours.
 * If the store hasn't opened yet today, returns today's opening hours.
 * If the store has already closed for the day, returns tomorrow's opening hours.
 *
 * @param {IRestaurant} store - The store to get opening hours for
 * @param {ServiceMode} serviceMode - The service mode to get opening hours for
 * @returns {string} The next opening hours in HH:mm:ss format
 */
export function getNextOpeningHoursForServiceMode(
  store: IRestaurant,
  serviceMode: ServiceMode
): string | null {
  const operatingHours = getStoreOperatingHoursForServiceMode(store, serviceMode);
  const storeTimeZone = store.timezone || undefined;

  if (!operatingHours) {
    return null;
  }

  const nowInStoreTimeZone = getCurrentDateInStoreTimeZone(store);

  const [todayOpenHours] = getTodaysHoursOfOperation(operatingHours, storeTimeZone);

  if (!todayOpenHours) {
    return getNextDayOpeningHours(operatingHours, nowInStoreTimeZone, storeTimeZone);
  }

  const todayOpenTime = getDateForHours(todayOpenHours, storeTimeZone);

  if (isBefore(nowInStoreTimeZone, todayOpenTime)) {
    return todayOpenHours;
  }

  return getNextDayOpeningHours(operatingHours, nowInStoreTimeZone, storeTimeZone);
}
